// Bodies (phase B, gate B5 and the hit reactions): the sim side of the figures' action poses, hit reactions and
// ragdolls, as one kernel module any scene can add. It listens to the combat seams and keeps plain data:
//
// - `combat:move` → the entity's pose track plays the move's pose (the poses table: a pose with the move's id, or one
//   whose `moves` patterns match), phase by phase: the windup holds at its end until the active phase starts, so the
//   strike key lands on the hit window (@rpg/figure's pose layer);
// - `combat:hit` → the reactions table picks a reaction (a flinch, a stagger, a knockback, a parried attacker's
//   stun), turned toward the blow; a knockback is handed to the game (`lookup.knockback`), which moves the body;
// - `combat:defeated` → the entity's card's part list becomes a ragdoll: bodies seeded from the card's rest under the
//   track's pose at this step (deterministic: the same sim time gives the same seed), the blow's impulse, then
//   falling, resting, dissolving and gone (its bodies removed), stepped after the physics step.
//
// In a streamed world (`chunkOf` given: the default scene), a ragdoll lying in a chunk that unloads goes with it at once
// (its bodies removed, gone as a dissolve's end; phase D joined): the ground under it is leaving the physics world.
// Tracks and ragdolls live in one kernel component (hashed) and the module's save slice; the Rapier bodies and joints
// are in the collision world's snapshot. Driven by the events, never by combat code: combat says what happened.
// Which entity has which card, where its root is and what a knockback moves are the scene's (`BodyLookup`).
import type { CollisionWorld } from "@rpg/collision";
import {
  chooseReaction,
  createPoseLibrary,
  entryEnd,
  figurePoseFromCard,
  phasePose,
  playPose,
  poseTrack,
  prunePoses,
  reactionDir,
  resolveRagdoll,
  samplePoses,
  spawnRagdoll,
  stepRagdoll,
  stopPose,
  upperJoints,
  type ActionPose,
  type FigureCard,
  type PartList,
  type PoseLibrary,
  type PoseSample,
  type PoseTrack,
  type RagdollState,
  type ReactionRule,
  type ResolvedRagdoll,
} from "@rpg/figure";
import type { GameModule } from "@rpg/kernel";
import type { SpikeContent } from "../content";
import type { CombatHit, GameEvents, Vec3 } from "../core";

/** The component (on one entity) holding the module's state; scenes list it in their hashed types. */
export const BODIES_C = "bodies/State";

/** What the module needs to know about the scene's figures. */
export interface BodyLookup {
  /** The card a figure entity is built from, or null (no figure, or not standing). */
  card(entity: number): string | null;
  /** Its root now: feet position, yaw, velocity. Null when it has none. */
  root(entity: number): { position: Vec3; yaw: number; velocity?: Vec3 } | null;
  /** A reaction knocks it back along `dir` (unit, flat) by `distance` over `seconds`: the scene moves it. */
  knockback?(entity: number, kb: { dir: Vec3; distance: number; seconds: number }): void;
  /** It became ragdoll `id`: the scene stops driving it (its capsule, its AI). */
  defeated?(entity: number, id: string): void;
  /** Its ragdoll is gone (dissolved and removed). */
  gone?(entity: number, id: string): void;
}

/** One live ragdoll. */
export interface LiveRagdoll {
  entity: number;
  card: string;
  state: RagdollState;
}

/** The module's state: plain data, saved and hashed. */
export interface BodiesState {
  /** Sim seconds since the scene began (the tracks' clock). */
  t: number;
  /** Ragdolls made so far (their ids). */
  seq: number;
  /** [entity, its pose track]. */
  tracks: [number, PoseTrack][];
  ragdolls: LiveRagdoll[];
}

/** The tables the module reads. */
export interface BodiesTables {
  poses: (ActionPose & { moves?: string[] })[];
  reactions: ReactionRule[];
  /** Part lists by card id. */
  ragdolls: Map<string, PartList>;
  cards: Map<string, FigureCard>;
}

/** Read the bodies' tables from the content. */
export function bodiesTables(content: SpikeContent): BodiesTables {
  const reg = content.registry;
  return {
    poses: reg.rows("poses") as unknown as BodiesTables["poses"],
    reactions: reg.rows("reactions") as unknown as ReactionRule[],
    ragdolls: new Map(reg.rows("ragdolls").map((r) => [r.card.id, r as unknown as PartList])),
    cards: new Map(reg.rows("cards").map((c) => [c.id, c as unknown as FigureCard])),
  };
}

/** What `createBodies` gives the scene. */
export interface Bodies {
  module: GameModule<GameEvents>;
  library: PoseLibrary;
  tables: BodiesTables;
  /** The live state (the component's object). */
  state(): BodiesState;
  /** A card's part list resolved (cached). */
  resolved(card: string): ResolvedRagdoll | null;
  /** A card's upper-body joint set (cached). */
  upper(card: string): Set<string>;
  /** An entity's track (empty when none). */
  track(entity: number): PoseTrack;
  /** An entity's track sampled at time `t` (the view samples at the frame's time). */
  sample(entity: number, card: string, t: number): PoseSample;
  /** The pose a combat move plays (its id, or a `moves` pattern), or null. */
  poseForMove(move: string): string | null;
  /** Play a named pose on an entity's track now, in the sim step (poses the game starts itself: a gather, a lever
   *  pull; phase C). False when the pose is unknown or the entity is a ragdoll. */
  play(entity: number, pose: string): boolean;
}

const LIFT = 0.02; // a seed lifted clear of the ground it stands on
/** Where a ragdoll part's reach is tested against an unloading chunk: its centre and 1.5 m round it (no part is longer). */
const CHUNK_REACH: readonly (readonly [number, number])[] = [[0, 0], [1.5, 0], [-1.5, 0], [0, 1.5], [0, -1.5], [1.1, 1.1], [1.1, -1.1], [-1.1, 1.1], [-1.1, -1.1]];

const globToRe = (g: string): RegExp => new RegExp(`^${g.split("*").map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*")}$`);

/** The bodies module for a scene: its physics world, tables, the layer ragdolls collide on and the scene's lookup. */
export function createBodies(o: { collision: CollisionWorld; tables: BodiesTables; layer: string; lookup: BodyLookup; chunkOf?: (x: number, z: number) => string | null }): Bodies {
  const { collision, tables, lookup } = o;
  const library = createPoseLibrary(tables.poses);
  const patterns = tables.poses.flatMap((p) => (p.moves ?? []).map((m) => ({ re: globToRe(m), pose: p.id })));
  const resolvedCache = new Map<string, ResolvedRagdoll | null>();
  const upperCache = new Map<string, Set<string>>();
  const state: BodiesState = { t: 0, seq: 0, tracks: [], ragdolls: [] };

  const resolved = (card: string): ResolvedRagdoll | null => {
    if (!resolvedCache.has(card)) {
      const c = tables.cards.get(card);
      const list = tables.ragdolls.get(card);
      resolvedCache.set(card, c && list ? resolveRagdoll(c, list) : null);
    }
    return resolvedCache.get(card)!;
  };
  const upper = (card: string): Set<string> => {
    let u = upperCache.get(card);
    if (!u) upperCache.set(card, (u = upperJoints(tables.cards.get(card) ?? { joints: [] })));
    return u;
  };
  const track = (entity: number): PoseTrack => {
    let e = state.tracks.find(([id]) => id === entity);
    if (!e) state.tracks.push((e = [entity, poseTrack()]));
    return e[1];
  };
  const ragdolled = (entity: number): boolean => state.ragdolls.some((r) => r.entity === entity);
  const poseForMove = (move: string): string | null => (library.has(move) ? move : (patterns.find((p) => p.re.test(move))?.pose ?? null));

  const onMove = ({ entity, move, phase }: GameEvents["combat:move"]): void => {
    if (ragdolled(entity) || lookup.card(entity) === null) return;
    const pose = poseForMove(move);
    if (!pose) return;
    const tr = track(entity);
    if (phase === "end") return stopPose(tr, pose, state.t);
    const p = library.get(pose);
    if (p.phases?.includes(phase)) phasePose(tr, library, pose, phase, state.t);
    else if (phase === "windup") playPose(tr, library, pose, state.t);
  };

  const onHit = (hit: CombatHit): void => {
    const r = chooseReaction(tables.reactions, { ...hit });
    if (!r || ragdolled(r.entity) || !library.has(r.pose)) return;
    const root = lookup.root(r.entity);
    if (!root || lookup.card(r.entity) === null) return;
    const total = library.get(r.pose).beats.reduce((a, b) => a + b, 0);
    playPose(track(r.entity), library, r.pose, state.t, {
      weight: r.weight,
      dir: r.blowYaw === null ? 0 : reactionDir(r.blowYaw, root.yaw),
      speed: r.seconds ? total / r.seconds : 1,
      ...(r.mask ? { mask: r.mask } : {}),
    });
    if (r.knockback && r.knockback.distance > 0) lookup.knockback?.(r.entity, r.knockback);
  };

  const onDefeated = (d: GameEvents["combat:defeated"]): void => {
    if (ragdolled(d.entity)) return;
    const card = lookup.card(d.entity);
    const root = lookup.root(d.entity);
    if (card === null || !root) return;
    const rd = resolved(card);
    const c = tables.cards.get(card);
    if (!rd || !c) return;
    const sample = samplePoses(track(d.entity), library, state.t, upper(card));
    const pose = figurePoseFromCard(c, { position: [root.position[0], root.position[1] + LIFT, root.position[2]], yaw: root.yaw }, sample);
    const id = `rd${++state.seq}`;
    const s = spawnRagdoll(collision, rd, { id, layer: o.layer, pose, ...(root.velocity ? { velocity: root.velocity } : {}), hit: { dir: d.dir, severity: d.severity, point: d.point } });
    state.ragdolls.push({ entity: d.entity, card, state: s });
    state.tracks = state.tracks.filter(([e]) => e !== d.entity);
    lookup.defeated?.(d.entity, id);
  };

  const module: GameModule<GameEvents> = {
    id: "bodies",
    version: "0.1.0",
    setup(ctx) {
      ctx.world.addComponent<BodiesState>(ctx.world.createEntity(), BODIES_C, state);
      ctx.events.on("combat:move", onMove);
      ctx.events.on("combat:hit", onHit);
      ctx.events.on("combat:defeated", onDefeated);
      ctx.registerSystem(({ dt }) => void (state.t += dt), { name: "bodies: clock", order: -50 });
      // a streamed world: a chunk unloading takes the ragdolls with any part over it, or within a part's reach of it (a
      // part centred over the next chunk can hang across the edge: the page's play-through saw a shade's hem do so)
      const chunkOf = o.chunkOf;
      if (chunkOf)
        ctx.events.on("world:chunk", (e) => {
          if (e.state !== "unloading") return;
          const over = (x: number, z: number): boolean => {
            for (const [dx, dz] of CHUNK_REACH) if (chunkOf(x + dx, z + dz) === e.id) return true;
            return false;
          };
          const going = state.ragdolls.filter(
            (r) =>
              r.state.phase !== "gone" &&
              r.state.bodies.some((b) => {
                if (!collision.has(b)) return false;
                const p = collision.pose(b).position;
                return over(p.x, p.z);
              }),
          );
          for (const r of going) {
            for (const b of r.state.bodies) if (collision.has(b)) collision.remove(b);
            r.state.phase = "gone";
            r.state.phaseAt = r.state.age;
            lookup.gone?.(r.entity, r.state.id);
          }
          if (going.length) state.ragdolls = state.ragdolls.filter((x) => x.state.phase !== "gone");
        });
      // after the scene's physics step (order 100): the ragdolls' phases, the gone removed; old poses pruned
      ctx.registerSystem(
        ({ dt }) => {
          for (const r of state.ragdolls) stepRagdoll(collision, r.state, resolved(r.card)!, dt);
          for (const r of state.ragdolls.filter((x) => x.state.phase === "gone")) lookup.gone?.(r.entity, r.state.id);
          state.ragdolls = state.ragdolls.filter((x) => x.state.phase !== "gone");
          for (const [, tr] of state.tracks) prunePoses(tr, library, state.t);
          state.tracks = state.tracks.filter(([, tr]) => tr.entries.length > 0);
        },
        { name: "bodies: ragdolls", order: 110 },
      );
      ctx.registerSaveSlice({
        serialize: () => structuredClone(state),
        deserialize: (data) => {
          const d = structuredClone(data as BodiesState);
          state.t = d.t;
          state.seq = d.seq;
          state.tracks = d.tracks;
          state.ragdolls = d.ragdolls;
        },
      });
    },
  };

  return {
    module,
    library,
    tables,
    state: () => state,
    resolved,
    upper,
    track: (entity) => state.tracks.find(([e]) => e === entity)?.[1] ?? poseTrack(),
    sample: (entity, card, t) => samplePoses(state.tracks.find(([e]) => e === entity)?.[1] ?? poseTrack(), library, t, upper(card)),
    poseForMove,
    play: (entity, pose) => {
      if (!library.has(pose) || ragdolled(entity)) return false;
      playPose(track(entity), library, pose, state.t);
      return true;
    },
  };
}

/** Whether an entry of a track is still playing at `t` (for the view's "acting" arms and the specs). */
export function playing(track: PoseTrack, lib: PoseLibrary, t: number): string[] {
  return track.entries.filter((e) => (e.stop === null || t - e.stop < lib.get(e.pose).blend.out) && t < entryEnd(e, lib.get(e.pose))).map((e) => e.pose);
}
